/**
 * BOM List Page
 * Manufacturing Module — card-based list with type tabs, search, and filtering
 *
 * Features:
 *   - Type tabs: ALL, ASSEMBLY, KIT, REFURBISHMENT, CUSTOM (with counts)
 *   - Card-based layout with badges, line counts, status
 *   - Search by code/description
 *   - Actions: View, Edit, Delete
 *   - "+ Uj BOM" button
 */

import type { Bom, BomStatus, BomType } from '@/api/manufacturing';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useBoms, useDeleteBom } from '@/hooks/use-manufacturing';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── BOM Type Config ────────────────────────────────────────────────────────

type TabKey = 'ALL' | BomType;

const BOM_TYPES: BomType[] = ['ASSEMBLY', 'KIT', 'REFURBISHMENT', 'CUSTOM'];

const TYPE_COLORS: Record<BomType, string> = {
  ASSEMBLY: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  KIT: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  REFURBISHMENT: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  CUSTOM: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const STATUS_COLORS: Record<BomStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  INACTIVE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function BomListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Build API filters
  const apiFilters = useMemo(() => {
    const filters: { bomType?: BomType; search?: string } = {};
    if (activeTab !== 'ALL') {
      filters.bomType = activeTab;
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }
    return filters;
  }, [activeTab, searchTerm]);

  const { boms, isLoading, error, refetch } = useBoms(apiFilters);
  const { mutate: doDelete } = useDeleteBom();

  // Count per type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: boms.length };
    for (const type of BOM_TYPES) {
      counts[type] = boms.filter(b => b.bomType === type).length;
    }
    return counts;
  }, [boms]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('common.confirmDelete') ?? 'Are you sure you want to delete this item?'))
      return;
    try {
      await doDelete(id);
      await refetch();
    } catch {
      // Error is handled in hook
    }
  };

  const tabs: { key: TabKey; labelKey: string }[] = [
    { key: 'ALL', labelKey: 'common.all' },
    ...BOM_TYPES.map(type => ({
      key: type as TabKey,
      labelKey: `manufacturing.bomType.${type.toLowerCase()}`,
    })),
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{t('manufacturing.bom.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('manufacturing.bom.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/manufacturing/bom/new')}
            className="bg-primary hover:bg-primary/90"
          >
            + {t('manufacturing.bom.createNew')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Type Tabs */}
        <div className="mb-6 flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-foreground shadow dark:bg-[#1a4f5c]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.labelKey)}
              <span
                className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-primary/10 text-primary'
                    : 'bg-gray-200 text-muted-foreground dark:bg-gray-700'
                }`}
              >
                {typeCounts[tab.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder={t('manufacturing.bom.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div>
        )}

        {/* Error */}
        {error && (
          <div className="py-12 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button variant="ghost" onClick={() => void refetch()} className="mt-2">
              {t('common.retry')}
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && boms.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            <p>{t('manufacturing.bom.noBoms')}</p>
          </div>
        )}

        {/* BOM Cards Grid */}
        {!isLoading && !error && boms.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {boms.map(bom => (
              <BomCard
                key={bom.id}
                bom={bom}
                onDetails={() => navigate(`/manufacturing/bom/${bom.id}`)}
                onEdit={() => navigate(`/manufacturing/bom/${bom.id}`)}
                onDelete={() => void handleDelete(bom.id)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Count */}
        {!isLoading && boms.length > 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            {t('manufacturing.bom.displayedCount', { count: String(boms.length) })}
          </p>
        )}
      </main>
    </div>
  );
}

// ─── BOM Card ───────────────────────────────────────────────────────────────

interface BomCardProps {
  bom: Bom;
  onDetails: () => void;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}

function BomCard({ bom, onDetails, onEdit, onDelete, t }: BomCardProps) {
  return (
    <Card className="group cursor-pointer transition-shadow hover:shadow-md" onClick={onDetails}>
      <CardContent className="pt-6">
        {/* Header: Type badge + Status */}
        <div className="mb-3 flex items-start justify-between">
          <Badge className={TYPE_COLORS[bom.bomType]}>
            {t(`manufacturing.bomType.${bom.bomType.toLowerCase()}`)}
          </Badge>
          <Badge className={STATUS_COLORS[bom.status]}>
            {t(`manufacturing.status.${bom.status.toLowerCase()}`)}
          </Badge>
        </div>

        {/* Code + Description */}
        <h3 className="mb-1 text-lg font-semibold text-foreground">{bom.bomCode}</h3>
        {bom.description && <p className="mb-2 text-sm text-muted-foreground">{bom.description}</p>}

        {/* Output product */}
        <div className="mb-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            {t('manufacturing.bom.outputProduct')}:
          </span>{' '}
          {bom.outputProductName}
        </div>

        {/* Line count + version */}
        <div className="mb-3 flex items-center gap-4 text-sm text-muted-foreground">
          <span>{t('manufacturing.bom.lineCount', { count: String(bom.lineCount) })}</span>
          <span>v{bom.version}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t pt-3 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onDetails();
            }}
          >
            {t('common.details')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onEdit();
            }}
          >
            {t('common.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 dark:text-red-400"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
